import { useState } from "react";
import { Plus, Minus, Check, Users, RotateCcw } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { AvatarBubble } from "../../../components/ui/voter-avatars";
import { getUserLabel } from "../../../lib/identity";
import {
  OVERLAY_PALETTE_DEFAULT,
  OVERLAY_PALETTE_EXTENDED,
} from "../utils/owner-tint";

/**
 * Phase 4b — "Add overlay" picker for the Create/Edit Poll page.
 *
 * Lists every poll-invitee friend who has an active incoming share with
 * the signed-in viewer. Caller is responsible for filtering (the picker
 * is purely presentational over the candidate list).
 *
 * Props:
 *   candidates       : Array<{ ownerId, displayName, avatarUrl, email? }>
 *   activeOwnerIds   : Set<string> | string[] — currently-overlaid owners
 *   onToggle         : (ownerId: string) => void — caller mutates active set
 *   colorByOwnerId   : map<ownerId, hex> — fully-resolved colors for active
 *                      overlays. Active rows render a color dot the user can
 *                      click to override; passing `null/undefined` for a
 *                      given uid hides the dot.
 *   onColorChange    : (ownerId, hex|null) => void — `null` clears override
 *   disabled         : boolean — disables trigger entirely (e.g. no candidates)
 *
 * Empty candidate list → renders a disabled trigger with explanatory text.
 */
export function AvailabilityOverlayPicker({
  candidates = [],
  activeOwnerIds = [],
  onToggle,
  colorByOwnerId = {},
  onColorChange,
  showMyAvailability = false,
  onShowMyAvailabilityChange,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const activeSet =
    activeOwnerIds instanceof Set ? activeOwnerIds : new Set(activeOwnerIds);
  const hasCandidates = candidates.length > 0;
  const hasSelfToggle = typeof onShowMyAvailabilityChange === "function";
  // If the caller has wired the self-availability toggle, the picker is
  // useful even with zero friend candidates — keep the trigger enabled.
  const isDisabled = disabled || (!hasCandidates && !hasSelfToggle);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={isDisabled}
          data-testid="availability-overlay-picker-trigger"
          className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <Plus className="h-3 w-3" />
          Add overlay
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-0"
        data-testid="availability-overlay-picker-content"
      >
        {hasSelfToggle ? (
          <label
            className="flex cursor-pointer items-center gap-2 border-b border-slate-100 px-4 py-3 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-slate-800"
            data-testid="overlay-show-self-row"
          >
            <input
              type="checkbox"
              checked={!!showMyAvailability}
              onChange={(e) =>
                onShowMyAvailabilityChange?.(e.target.checked)
              }
              data-testid="overlay-show-self-checkbox"
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus-visible:ring-2 focus-visible:ring-ring"
            />
            <span>Show my availability</span>
          </label>
        ) : null}
        <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Show friends' availability
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
            Only friends invited to this poll who share their calendar with you appear here.
          </p>
        </div>
        {!hasCandidates ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <Users className="h-5 w-5 text-slate-400" />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              No friends invited to this poll have shared their calendar with you yet.
            </p>
          </div>
        ) : (
          <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
            {candidates.map((candidate) => {
              const isActive = activeSet.has(candidate.ownerId);
              const color = colorByOwnerId?.[candidate.ownerId] || null;
              // Privacy: list the candidate's CHOSEN public identifier — never
              // their raw email (which rides along on the object). getUserLabel
              // returns the email ONLY for email-type users.
              const name = getUserLabel(candidate) || "Friend";
              return (
                <li
                  key={candidate.ownerId}
                  className="flex items-center gap-2 px-4 py-2 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  <button
                    type="button"
                    onClick={() => {
                      onToggle?.(candidate.ownerId);
                      // Close the picker after a friend toggle so the user
                      // can immediately interact with the resulting chip /
                      // its color dot. Without this, a follow-up
                      // trigger.click() (e.g. the e2e suite re-opening to
                      // tweak color) toggles the still-open popover closed
                      // and detaches the dot mid-click.
                      setOpen(false);
                    }}
                    data-testid={`overlay-picker-row-${candidate.ownerId}`}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    aria-pressed={isActive}
                  >
                    <AvatarBubble
                      user={{
                        displayName: candidate.displayName,
                        email: candidate.email,
                        photoURL: candidate.avatarUrl,
                        publicIdentifier: candidate.publicIdentifier,
                        publicIdentifierType: candidate.publicIdentifierType,
                        qsUsername: candidate.qsUsername,
                        discordUsername: candidate.discordUsername,
                      }}
                      label={name}
                      size={24}
                    />
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-700 dark:text-slate-200">
                      {name}
                    </span>
                  </button>
                  {isActive && color && onColorChange ? (
                    <OverlayColorDot
                      ownerId={candidate.ownerId}
                      color={color}
                      onChange={onColorChange}
                    />
                  ) : null}
                  {isActive ? (
                    <Check className="h-4 w-4 shrink-0 text-emerald-600" />
                  ) : (
                    <Plus className="h-4 w-4 shrink-0 text-slate-400" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Color-dot trigger + nested swatch popover. Clicking the dot opens an
 * 8-swatch panel with a (+) toggle that reveals the extended 16. A Reset
 * button clears any explicit override (caller passes `null` for hex).
 */
function OverlayColorDot({ ownerId, color, onChange }) {
  const [open, setOpen] = useState(false);
  const [showExtended, setShowExtended] = useState(false);

  const handlePick = (hex) => {
    onChange?.(ownerId, hex);
    setOpen(false);
  };
  const handleReset = () => {
    onChange?.(ownerId, null);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setShowExtended(false);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Change overlay color"
          data-testid={`overlay-color-dot-${ownerId}`}
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-slate-300 transition-transform motion-safe:hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 dark:border-slate-600"
          style={{ backgroundColor: color, boxShadow: `0 0 0 1px ${color}` }}
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-auto p-3"
        data-testid={`overlay-color-swatches-${ownerId}`}
      >
        <div className="flex flex-wrap gap-1.5">
          {OVERLAY_PALETTE_DEFAULT.map((hex) => (
            <Swatch
              key={hex}
              hex={hex}
              selected={color === hex}
              onClick={() => handlePick(hex)}
              testId={`overlay-color-swatch-${ownerId}-${hex.slice(1)}`}
            />
          ))}
          {showExtended
            ? OVERLAY_PALETTE_EXTENDED.map((hex) => (
                <Swatch
                  key={hex}
                  hex={hex}
                  selected={color === hex}
                  onClick={() => handlePick(hex)}
                  testId={`overlay-color-swatch-${ownerId}-${hex.slice(1)}`}
                />
              ))
            : null}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2 dark:border-slate-800">
          <button
            type="button"
            onClick={() => setShowExtended((v) => !v)}
            data-testid={`overlay-color-toggle-extended-${ownerId}`}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            aria-expanded={showExtended}
          >
            {showExtended ? (
              <>
                <Minus className="h-3 w-3" />
                Fewer
              </>
            ) : (
              <>
                <Plus className="h-3 w-3" />
                More colors
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleReset}
            data-testid={`overlay-color-reset-${ownerId}`}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Swatch({ hex, selected, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label={`Pick color ${hex}`}
      aria-pressed={selected}
      className="relative inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 transition-transform motion-safe:hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 dark:border-slate-700"
      style={{ backgroundColor: hex }}
    >
      {selected ? (
        <Check className="h-3 w-3 text-white drop-shadow" aria-hidden="true" />
      ) : null}
    </button>
  );
}
